import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { FieldArray, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { UserSelector } from "@/core/components/user-selector";
import { Button } from "@volca/ui/components/ui/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useBusiness } from "@/core/business";
import { useUpdateReviewUsers } from "@/reviews/query";

interface ReviewAttributionContentProps {
  reviewId: string;
  onClose: () => void;
}

interface ReviewAttributionFormValues {
  userIds: string[];
}

const INITIAL_VALUES: ReviewAttributionFormValues = {
  userIds: [""],
};

const validationSchema = Yup.object().shape({
  userIds: Yup.array()
    .of(Yup.string())
    .min(1, "At least one user is required")
    .test("no-duplicates", "Duplicate users are not allowed", (value) => {
      if (!value) return true;
      const nonEmpty = value.filter((userId) => userId !== "");
      return nonEmpty.length === new Set(nonEmpty).size;
    })
    .test(
      "at-least-one-selected",
      "At least one user must be selected",
      (value) => {
        if (!value) return false;
        return value.some((userId) => userId !== "");
      },
    ),
});

export const ReviewAttributionContent = ({
  reviewId,
  onClose,
}: ReviewAttributionContentProps) => {
  const { businessId } = useBusiness();
  const { mutateAsync: updateReviewUsers, isPending } =
    useUpdateReviewUsers(businessId);

  const handleSubmit = async (
    values: ReviewAttributionFormValues,
    { setSubmitting }: FormikHelpers<ReviewAttributionFormValues>,
  ) => {
    try {
      const filteredUserIds = values.userIds.filter((userId) => userId !== "");

      if (filteredUserIds.length === 0) {
        toast.error("Please select at least one user");
        setSubmitting(false);
        return;
      }

      await updateReviewUsers({ reviewId, userIds: filteredUserIds });

      toast.success("Review attribution updated");
      onClose();
    } catch {
      toast.error("Failed to update review attribution");
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add users to review</DialogTitle>
      </DialogHeader>

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Stack align="start" gap={4} className="w-full p-6 pt-0">
              <FieldArray name="userIds">
                {({ push, remove }) => (
                  <Stack className="w-full" align="start" gap={2}>
                    {values.userIds.map((userId, index) => (
                      <Stack
                        className="w-full"
                        key={index}
                        direction="row"
                        align="center"
                        gap={2}
                      >
                        <UserSelector
                          businessId={businessId}
                          value={userId}
                          onChange={(value) =>
                            setFieldValue(`userIds.${index}`, value)
                          }
                          placeholder="Select a user"
                          fullWidth
                        />
                        {values.userIds.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Remove user"
                            onClick={() => remove(index)}
                            className="mb-1 shrink-0"
                          >
                            <XIcon className="size-5 text-subtle" />
                          </Button>
                        )}
                      </Stack>
                    ))}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => push("")}
                    >
                      Add user
                    </Button>
                  </Stack>
                )}
              </FieldArray>
              {errors.userIds && touched.userIds && (
                <Text variant="caption" tone="destructive">
                  {typeof errors.userIds === "string"
                    ? errors.userIds
                    : "Please check user selections"}
                </Text>
              )}
            </Stack>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </>
  );
};
